<script setup>
import axios from 'axios';
import { BSpinner } from 'bootstrap-vue-next';
import { ref, watch } from 'vue';
import { useRoute } from 'vue-router';

import MarkdownRenderer from '~/common/components/MarkdownRenderer.vue';
import { DEFAULT_TITLE } from '~/common/Constants';

import NotFoundPage from './NotFoundPage.vue';

const props = defineProps({
  page: {
    type: String,
    required: true,
  },
});
const route = useRoute();

const content = ref('');
const renderedContent = ref('');
const notFound = ref(false);

const setPageTitle = () => {
  // Find the first H1 heading in the content
  const lines = content.value.split('\n');
  const headingLine = lines.find(line => line.trim().startsWith('# '));
  if (headingLine) {
    document.title = `${headingLine.substring(2)} - ${DEFAULT_TITLE}`;
  } else {
    const page = props.page.split('/').pop().replace(/-/g, ' ');
    document.title = `${page.charAt(0).toUpperCase() + page.slice(1)} - ${DEFAULT_TITLE}`;
  }
};

const scrollToAnchor = () => {
  const anchor = location.hash;
  if (anchor) {
    location.hash = '';
    setTimeout(() => {
      location.hash = anchor;
    }, 0);
  }
};

const loadContent = async () => {
  const response = await axios.get(`/docs/${props.page}.md`);
  if (response.headers['content-type'] === 'text/plain') {
    content.value = response.data;

    notFound.value = false;

    setPageTitle();
    scrollToAnchor();
  } else {
    notFound.value = true;
  }
};

watch(() => props.page, () => {
  content.value = '';
  renderedContent.value = '';
  notFound.value = false;

  loadContent();
});

watch(() => route.hash, (newHash) => {
  if (newHash) {
    const target = document.querySelector(newHash);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth' });
    }
  }
});

loadContent();
</script>

<template>
  <div>
    <NotFoundPage v-if="notFound" />
    <MarkdownRenderer
      v-else-if="content"
      :markdown-content="content"
      :use-marked-renderer="true"
    />
    <div
      v-else
      class="loading"
    >
      <BSpinner />
      <div>Loading documentation...</div>
    </div>
  </div>
</template>
